import { useState, useRef, useEffect, useMemo } from 'react';
import { arrayHasItems } from '../../helpers/common';
import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

export const OTBProductSelect = ({ 
    options, 
    selectedProduct,
    handleChange, 
    optionsIsLoading,
    placeholder = 'Select a product',
    // Style overrides
    containerClassName = '',
    buttonClassName = '',
    dropdownClassName = '',
    searchWrapperClassName = '',
    searchInputClassName = '',
    optionClassName = '',
    optionActiveClassName = 'bg-primary text-white font-semibold',
    noResultsClassName = ''
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [selectedOption, setSelectedOption] = useState(selectedProduct || null);
    const dropdownRef = useRef(null);

    const filteredOptions = useMemo(() => {
        return options?.filter((option) =>
            option.label.toLowerCase().includes(search.toLowerCase())
        ) || [];
    }, [search, options]);

    useEffect(() => {
        setSelectedOption(selectedProduct ?? null);
    }, [selectedProduct]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleOptionClick = (option) => {
        setSelectedOption(option);
        handleChange?.(option);
        setIsOpen(false);
        setSearch('');
    };

    return (
        <div className={`relative ${containerClassName}`} ref={dropdownRef}>
            <button
                onClick={() => setIsOpen((prev) => !prev)}
                type="button"
                className={`w-full px-4 py-2 text-left bg-white flex flex-row justify-between items-center border border-gray-300 rounded-md shadow-sm disabled:cursor-not-allowed disabled:bg-[#efefef4d] ${buttonClassName}`}
                disabled={optionsIsLoading}
            >
                {options?.find((option) => String(option.value) === String(selectedOption))?.label || placeholder}
                <div className='flex flex-row gap-2'>
                    <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
                </div>
            </button>
            {isOpen && (
                <div className={`absolute z-20 w-full mt-2 bg-white border border-gray-300 rounded-md shadow-lg ${dropdownClassName}`}>
                    <div className={`relative ${searchWrapperClassName}`}>
                        <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-1.5 -translate-y-1/2 top-1/2" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search products..."
                            className={`w-full px-4 py-2 pb-1.5 pl-8 border-b border-gray-200 focus:outline-none ${searchInputClassName}`}
                        />
                    </div>
                    <ul className="max-h-48 overflow-y-auto">
                        {arrayHasItems(filteredOptions) ? (
                                filteredOptions.map((option, index) => (
                                    <li
                                        key={index}
                                        onClick={() => handleOptionClick(option.value)}
                                        className={`flex justify-between px-4 py-2 cursor-pointer hover:bg-blue-200 ${String(selectedOption) === String(option.value) ? optionActiveClassName : ""} ${optionClassName}`}
                                    >
                                        {option.label}
                                    </li>
                                ))
                            ) : (
                                <div className={`${noResultsClassName}`}>
                                    <li className="px-4 py-2 text-gray-500 w-full text-center">No results found</li>
                                </div>
                            )}
                    </ul>
                </div>
            )}
        </div>
    );
};